/**
 * The company's roles (docs/design/2026-09-16-custom-roles.md): name a role, tick what it
 * may do, save, and give it to people on their employee card.
 *
 * Three things this screen refuses to pretend. It offers only permissions the server sent,
 * so no checkbox exists that the server would not enforce. It says once, in words, that a
 * person's own working life is not on this list, rather than drawing a row of ticked
 * checkboxes nobody may untick. And it shows how many people hold a role beside the Save,
 * because reducing a role is a real change to those people and they should be counted
 * before it happens, not warned about in a dialog afterwards.
 */
import { ApiError } from '@/api/client';
import { type ApiPermission, type ApiRole, useRoleWrites, useRoles } from '@/api/data';
import { T } from '@/i18n';
import { Button, Panel, Skeleton, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { useState } from 'react';

/** A role being edited: what is on screen, against the revision it was read at. */
type Draft = { key: string; name: string; permissions: string[]; revision: number; isNew: boolean };

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[æå]/g, 'a')
    .replace(/ø/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);

/** Everything `want` rests on, the same closure the server applies when it saves. */
function withRequired(want: string[], all: ApiPermission[]): string[] {
  const by = new Map(all.map((p) => [p.key, p]));
  const out = new Set<string>();
  const add = (k: string) => {
    if (!by.has(k) || out.has(k)) return;
    out.add(k);
    for (const r of by.get(k)?.requires ?? []) add(r);
  };
  for (const k of want) add(k);
  return all.map((p) => p.key).filter((k) => out.has(k));
}

/** Everything that would be left dangling if `key` went away. */
function withoutDependents(have: string[], key: string, all: ApiPermission[]): string[] {
  const by = new Map(all.map((p) => [p.key, p]));
  const gone = new Set([key]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const k of have) {
      if (gone.has(k)) continue;
      if ((by.get(k)?.requires ?? []).some((r) => gone.has(r))) {
        gone.add(k);
        changed = true;
      }
    }
  }
  return have.filter((k) => !gone.has(k));
}

export default function Roller() {
  const q = useRoles();
  const w = useRoleWrites();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [problem, setProblem] = useState('');

  const answer = q.data;
  const permissions = answer?.permissions ?? [];
  const mayEdit = !!answer?.may_edit;

  const open = (r: ApiRole) =>
    setDraft({
      key: r.key,
      name: r.name,
      permissions: [...r.permissions],
      revision: r.revision,
      isNew: false,
    });
  const openNew = () => setDraft({ key: '', name: '', permissions: [], revision: 0, isNew: true });
  const close = () => {
    setDraft(null);
    setProblem('');
  };

  const toggle = (key: string) => {
    if (!draft) return;
    const has = draft.permissions.includes(key);
    // Unticking a prerequisite clears what rested on it here and now, rather than leaving a
    // form that says one thing and a saved policy that says another.
    setDraft({
      ...draft,
      permissions: has
        ? withoutDependents(draft.permissions, key, permissions)
        : withRequired([...draft.permissions, key], permissions),
    });
  };

  const save = async () => {
    if (!draft) return;
    setProblem('');
    try {
      if (draft.isNew) {
        const key = slug(draft.name);
        if (!key) return setProblem(T('Rollen trenger et navn.'));
        await w.create.mutateAsync({ key, name: draft.name.trim(), permissions: draft.permissions });
        toast(T('Rollen er opprettet'));
      } else {
        await w.update.mutateAsync({
          key: draft.key,
          body: { name: draft.name.trim(), permissions: draft.permissions, revision: draft.revision },
        });
        toast(T('Lagret'));
      }
      close();
    } catch (e) {
      setProblem(messageFor(e));
    }
  };

  const remove = async (r: ApiRole) => {
    setProblem('');
    try {
      await w.remove.mutateAsync(r.key);
      toast(T('Rollen er slettet'));
      close();
    } catch (e) {
      setProblem(messageFor(e));
    }
  };

  if (q.isLoading)
    return (
      <Panel>
        <Skeleton />
      </Panel>
    );

  return (
    <>
      <div className="al info">
        <span>ⓘ</span>
        <span>{answer?.baseline ?? ''}</span>
      </div>

      {!mayEdit && (
        <p className="hint" style={{ maxWidth: '66ch' }}>
          {T('Bare administratorer kan endre roller. Her ser du hva hver rolle gir.')}
        </p>
      )}

      {(answer?.roles ?? []).map((r) => {
        const editing = draft && !draft.isNew && draft.key === r.key;
        return (
          <Panel
            key={r.key}
            title={r.name}
            sub={`${r.members} ${r.members === 1 ? T('person') : T('personer')}`}
            actions={
              mayEdit &&
              !editing && (
                <Button small onClick={() => open(r)}>
                  {r.key === 'admin' ? T('Se tilganger') : T('Endre')}
                </Button>
              )
            }
          >
            <div className="cb">
              {r.key === 'admin' ? (
                <p style={{ margin: 0 }}>
                  {T(
                    'Administrator har alt, og kan ikke endres. Det er denne rollen som gir tilgang til roller, tilganger og bedriftens konto.',
                  )}
                </p>
              ) : (
                <p style={{ margin: 0 }}>
                  {r.permissions.length
                    ? permissions
                        .filter((p) => r.permissions.includes(p.key))
                        .map((p) => p.name)
                        .join(', ')
                    : T('Ingen tilganger utover det alle har.')}
                </p>
              )}
              {editing && draft && (
                <Editor
                  draft={draft}
                  permissions={permissions}
                  members={r.members}
                  problem={problem}
                  onName={(name) => setDraft({ ...draft, name })}
                  onToggle={toggle}
                  onSave={save}
                  onClose={close}
                  onDelete={r.is_system ? undefined : () => remove(r)}
                  readOnly={r.key === 'admin'}
                  busy={w.update.isPending || w.remove.isPending}
                />
              )}
            </div>
          </Panel>
        );
      })}

      {mayEdit && draft?.isNew && (
        <Panel title={T('Ny rolle')}>
          <div className="cb">
            <Editor
              draft={draft}
              permissions={permissions}
              members={0}
              problem={problem}
              onName={(name) => setDraft({ ...draft, name })}
              onToggle={toggle}
              onSave={save}
              onClose={close}
              busy={w.create.isPending}
            />
          </div>
        </Panel>
      )}

      {mayEdit && !draft && (
        <Button tone="pri" onClick={openNew}>
          <Icon name="plus" />
          {T('Ny rolle')}
        </Button>
      )}
    </>
  );
}

function Editor({
  draft,
  permissions,
  members,
  problem,
  onName,
  onToggle,
  onSave,
  onClose,
  onDelete,
  readOnly,
  busy,
}: {
  draft: Draft;
  permissions: ApiPermission[];
  members: number;
  problem: string;
  onName: (v: string) => void;
  onToggle: (k: string) => void;
  onSave: () => void;
  onClose: () => void;
  onDelete?: (() => void) | undefined;
  readOnly?: boolean | undefined;
  busy?: boolean | undefined;
}) {
  const by = new Map(permissions.map((p) => [p.key, p]));
  return (
    <>
      {problem && (
        <div className="al dgr" role="alert" data-testid="role-problem">
          <span>
            <Icon name="stop" />
          </span>
          <span>{problem}</span>
        </div>
      )}
      {!readOnly && (
        <div className="fld">
          <span className="lbl">{T('Navn')}</span>
          <input
            className="inp w"
            aria-label={T('Navn på rollen')}
            value={draft.name}
            onChange={(e) => onName(e.target.value)}
          />
        </div>
      )}
      <ul className="vlist" style={{ marginTop: 8 }}>
        {permissions.map((p) => {
          const on = draft.permissions.includes(p.key);
          const needs = p.requires.map((r) => by.get(r)?.name ?? r);
          return (
            <li key={p.key}>
              <label
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  className="cbx"
                  checked={on}
                  disabled={readOnly}
                  onChange={() => onToggle(p.key)}
                  aria-label={p.name}
                  style={{ accentColor: 'var(--act)', marginTop: 3 }}
                />
                <span>
                  <b>{p.name}</b>
                  <span className="hint" style={{ display: 'block', maxWidth: '66ch' }}>
                    {p.detail}
                    {needs.length ? ` ${T('Krever')}: ${needs.join(', ')}.` : ''}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {!readOnly && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          {onDelete && (
            <Button ghost tone="dgr" onClick={onDelete} disabled={busy}>
              <Icon name="trash" />
              {T('Slett')}
            </Button>
          )}
          <span className="sp" />
          {/* counted beside the Save, because this is what the change does to people */}
          <span className="hint">
            {members === 0
              ? T('Ingen har denne rollen ennå.')
              : members === 1
                ? T('1 person har denne rollen.')
                : T('{n} personer har denne rollen.', { n: members })}
          </span>
          <Button onClick={onClose} disabled={busy}>
            {T('Avbryt')}
          </Button>
          <Button tone="pri" onClick={onSave} disabled={busy || !draft.name.trim()}>
            {T('Lagre')}
          </Button>
        </div>
      )}
      {readOnly && (
        <div style={{ marginTop: 12 }}>
          <Button onClick={onClose}>{T('Lukk')}</Button>
        </div>
      )}
    </>
  );
}

/** The server's refusals, in the words of the thing the person was doing. */
function messageFor(e: unknown): string {
  if (!(e instanceof ApiError)) return T('Resultatet er ukjent. Les siden på nytt.');
  const body = (e.body ?? {}) as { error?: string; message?: string; members?: number };
  switch (body.error) {
    case 'revision_conflict':
      return T('Noen andre endret rollen mens du holdt på. Lukk og åpne den igjen for å se hva den er nå.');
    case 'role_in_use':
      return T('{n} har denne rollen. Gi dem en annen rolle først.', { n: body.members ?? 0 });
    case 'protected_role':
      return T('Administrator og Ansatt hører til enhver bedrift og kan ikke endres bort.');
    case 'reserved_key':
      return T('Det navnet er opptatt av en fast rolle. Velg et annet.');
    case 'key_taken':
      return T('Bedriften har allerede en rolle med det navnet.');
    case 'not_system_admin':
      return T('Du er ikke lenger administrator i denne bedriften.');
    default:
      return body.message || e.message;
  }
}
